import { Component, Inject, OnDestroy } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { Opportunity } from '@vendasta/sales-opportunities';
import { BehaviorSubject, Observable, finalize, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { OpportunityService } from '../../opportunity.service';
import { SALES_UI_OPPORTUNITY_DETAILS_CONFIG_TOKEN, SalesUIOpportunityDetailsConfig } from '../../tokens';
import { OpportunitySidePanelEventsService } from '../opportunity-side-panel-events.service';
import { Mode, OpportunityDetailsService } from './opportunity-details.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'sales-ui-opportunity-details',
  templateUrl: './opportunity-details.component.html',
  styleUrls: ['./opportunity-details.component.scss'],
  standalone: false,
})
export class OpportunityDetailsComponent implements OnDestroy {
  public readonly opportunity$: Observable<Opportunity>;
  public readonly accountGroupId$: Observable<string>;
  private readonly subscriptions = SubscriptionList.new();
  public readonly isEditable$: Observable<boolean>;
  readonly mode$: Observable<Mode>;
  readonly updateTagsLoading$$ = new BehaviorSubject<boolean>(false);
  readonly updateNoteLoading$$ = new BehaviorSubject<boolean>(false);

  private readonly forceReload$$ = new BehaviorSubject<boolean>(false);
  readonly forceReload$ = this.forceReload$$.asObservable();
  protected partnerId$: Observable<string>;
  protected marketId$: Observable<string>;
  protected bypassQuantityRestrictions$: Observable<boolean>;
  protected readonly showActivities$: Observable<boolean>;

  constructor(
    @Inject(SALES_UI_OPPORTUNITY_DETAILS_CONFIG_TOKEN) private readonly config: SalesUIOpportunityDetailsConfig,
    private readonly updates: OpportunityService,
    private readonly opportunityDetailsSlideoutService: OpportunityDetailsService,
    private readonly snackbarService: SnackbarService,
    private readonly sidePanelEvents: OpportunitySidePanelEventsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.partnerId$ = this.config.partnerId$;
    this.marketId$ = this.config.marketId$;
    this.bypassQuantityRestrictions$ = this.config.bypassQuantityRestrictions$;
    this.opportunity$ = this.updates.currentOpportunity$;
    this.accountGroupId$ = this.opportunity$.pipe(
      map((opportunity) => opportunity?.accountGroupId || ''),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.showActivities$ = this.config.showActivities$ ?? of(false);

    this.mode$ = this.opportunityDetailsSlideoutService.mode$;
    this.isEditable$ = this.updates.isEditable$();

    this.subscriptions.add(this.opportunity$, () => {
      this.forceReload$$.next(false);
      window.setTimeout(() => this.forceReload$$.next(true), 500);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  handleQualificationClicked(): void {
    this.opportunityDetailsSlideoutService.setMode('qualification');
  }

  async handleTagsChanged(tags: string[]): Promise<void> {
    this.updateTagsLoading$$.next(true);
    const work$ = this.updates.updateTags(tags).pipe(finalize(() => this.updateTagsLoading$$.next(false)));
    const res = await firstValueFrom(work$);
    if (!res) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_UPDATING_TAGS');
    }
  }

  closeSideNav(): void {
    this.sidePanelEvents.sidePanelCloseRequested();
    this.opportunityDetailsSlideoutService.setMode('details');
    this.removeOpportunityIDFromURL();
  }

  private removeOpportunityIDFromURL(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { opportunityId: null },
      queryParamsHandling: 'merge',
    });
  }

  async updateNote(newNote: string): Promise<void> {
    this.updateNoteLoading$$.next(true);
    const work$ = this.updates.updateNote(newNote).pipe(finalize(() => this.updateNoteLoading$$.next(false)));
    const res = await firstValueFrom(work$);
    if (!res) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.UNABLE_TO_UPDATE_OVERVIEW');
    }
  }
}
